import InteractiveMap from "@/components/forms/InteractiveMap";
import { Button, Callout, Card, FixedFooter, Text } from "@/components/ui";
import type { PinAnchor } from "@/lib/address-pin";
import { AddressSchemaType } from "@/types/forms";

/**
 * Map confirmation step. The pin drags inside the green circle of the typed address. Moving it does not change the street or district.
 */
export function AddressMapStep({
  fields,
  position,
  anchor,
  outOfReach,
  loading,
  onPinMove,
  onConfirm,
  onBackToDetails,
}: {
  fields: Pick<AddressSchemaType, "street" | "number" | "district" | "city" | "state" | "postalCode">;
  position?: { lat: number; lng: number };
  anchor: PinAnchor | null;
  outOfReach: boolean;
  loading: boolean;
  onPinMove: (lat: number, lng: number) => void;
  onConfirm: () => void;
  onBackToDetails: () => void;
}) {
  const reach =
    anchor && anchor.limitMeters !== null
      ? { lat: anchor.lat, lng: anchor.lng, radiusMeters: anchor.limitMeters, exceeded: outOfReach }
      : undefined;

  return (
    <>
      <Card padding="sm">
        <Text size="sm" weight="semibold" tone="default">
          {[fields.street, fields.number].filter(Boolean).join(", ")}
        </Text>
        <Text size="sm" tone="muted">
          {[fields.district, fields.city, fields.state, fields.postalCode].filter(Boolean).join(" · ")}
        </Text>
        <Text size="xs" tone="subtle" className="mt-1">
          {reach
            ? "Arraste o pin até a porta ou portaria, dentro do círculo verde. Rua e bairro não mudam."
            : "Arraste o pin até a porta ou portaria. Rua e bairro não mudam."}
        </Text>
      </Card>

      <div className="relative z-0 w-full h-map overflow-hidden rounded-card shadow-card">
        <InteractiveMap
          lat={position?.lat ?? 0}
          lng={position?.lng ?? 0}
          onPositionChange={onPinMove}
          disabled={loading}
          reach={reach}
          hint="Arraste o pin dentro do círculo verde. Confirme no botão abaixo."
        />
      </div>

      {outOfReach && (
        <Callout tone="warning" title="O pin ficou longe do endereço">
          Ele precisa ficar dentro do círculo do endereço que você informou. Se o endereço estiver errado, volte e
          corrija os dados.
        </Callout>
      )}

      <FixedFooter>
        <div className="flex flex-col gap-2 w-full">
          <Button
            type="button"
            size="lg"
            fullWidth
            disabled={loading || !position?.lat || outOfReach}
            onClick={onConfirm}
          >
            Confirmar localização
          </Button>
          <Button
            type="button"
            variant="outline"
            tone="neutral"
            size="md"
            fullWidth
            disabled={loading}
            onClick={onBackToDetails}
          >
            Voltar e editar endereço
          </Button>
        </div>
      </FixedFooter>
    </>
  );
}
